<!-- 会员等级、成长规则、积分规则与权益配置的展示。保存与编辑弹窗由父页面处理。 -->
<template>
  <section class="member-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>当前页支持真实配置读取与整表保存，保存时会批量提交当前列表。</span>
        </div>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('save')">保存配置</button>
        </div>
      </div>

      <div v-if="section === 'levels'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>等级编码</th>
              <th>等级名称</th>
              <th class="is-right">成长门槛</th>
              <th class="is-right">折扣系数</th>
              <th class="is-right">积分倍率</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in memberLevels" :key="item.id || item.code">
              <td><span class="cell-text">{{ item.code }}</span></td>
              <td><span class="cell-text">{{ item.name || '--' }}</span></td>
              <td class="is-right">{{ item.growthThreshold ?? 0 }}</td>
              <td class="is-right">{{ item.discountRate ?? 1 }}</td>
              <td class="is-right">{{ item.pointsMultiplier ?? 1 }}</td>
              <td class="is-right"><div class="row-actions"><button title="编辑等级" @click="emit('editLevel', item)"><el-icon><EditPen /></el-icon></button><button class="is-danger" title="删除等级" @click="emit('removeLevel', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!memberLevels.length && !loading"><td colspan="6"><el-empty description="暂无等级配置" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'growth'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>规则类型</th>
              <th>规则参数</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in growthRules" :key="item.id || item.ruleType">
              <td><span class="cell-text">{{ item.ruleType }}</span></td>
              <td><span class="cell-text">{{ item.param || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(item.enabled ? 'success' : 'muted')]">{{ item.enabled ? '启用' : '停用' }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="编辑规则" @click="emit('editGrowthRule', item)"><el-icon><EditPen /></el-icon></button><button class="is-danger" title="删除规则" @click="emit('removeGrowthRule', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!growthRules.length && !loading"><td colspan="4"><el-empty description="暂无成长规则" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'points'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>规则类型</th>
              <th>计算方式</th>
              <th class="is-right">规则值</th>
              <th class="is-right">单单上限</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pointsRules" :key="item.id || item.ruleType">
              <td><span class="cell-text">{{ item.ruleType }}</span></td>
              <td><span class="cell-text">{{ item.calcMethod || '--' }}</span></td>
              <td class="is-right">{{ item.value ?? 0 }}</td>
              <td class="is-right">{{ item.maxUsePerOrder ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(item.enabled ? 'success' : 'muted')]">{{ item.enabled ? '启用' : '停用' }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="编辑规则" @click="emit('editPointsRule', item)"><el-icon><EditPen /></el-icon></button><button class="is-danger" title="删除规则" @click="emit('removePointsRule', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!pointsRules.length && !loading"><td colspan="6"><el-empty description="暂无积分规则" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>适用等级</th>
              <th>权益类型</th>
              <th>权益配置</th>
              <th class="is-right">排序</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in memberBenefits" :key="item.id || `${item.levelCode}-${item.benefitType}`">
              <td><span class="cell-text">{{ item.levelCode }}</span></td>
              <td><span class="cell-text">{{ item.benefitType || '--' }}</span></td>
              <td><span class="cell-text">{{ item.config || '--' }}</span></td>
              <td class="is-right">{{ item.sort ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(item.enabled ? 'success' : 'muted')]">{{ item.enabled ? '启用' : '停用' }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="编辑权益" @click="emit('editBenefit', item)"><el-icon><EditPen /></el-icon></button><button class="is-danger" title="删除权益" @click="emit('removeBenefit', item)"><el-icon><Delete /></el-icon></button></div></td>
            </tr>
            <tr v-if="!memberBenefits.length && !loading"><td colspan="6"><el-empty description="暂无会员权益" /></td></tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { Delete, EditPen } from '@element-plus/icons-vue'
import type { GrowthRuleConfig, MemberBenefitConfig, MemberLevelConfig, PointsRuleConfig } from '@/api/member'
import type { OperationStat } from '@/data/operationDesign'

type MemberSection = 'levels' | 'growth' | 'points' | 'benefits'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: MemberSection
  stats: OperationStat[]
  memberLevels: MemberLevelConfig[]
  growthRules: GrowthRuleConfig[]
  pointsRules: PointsRuleConfig[]
  memberBenefits: MemberBenefitConfig[]
  loading: boolean
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  save: []
  editLevel: [item: MemberLevelConfig]
  editGrowthRule: [item: GrowthRuleConfig]
  editPointsRule: [item: PointsRuleConfig]
  editBenefit: [item: MemberBenefitConfig]
  removeLevel: [item: MemberLevelConfig]
  removeGrowthRule: [item: GrowthRuleConfig]
  removePointsRule: [item: PointsRuleConfig]
  removeBenefit: [item: MemberBenefitConfig]
}>()

const {
  section,
  stats,
  memberLevels,
  growthRules,
  pointsRules,
  memberBenefits,
  loading,
  toneClass,
} = toRefs(props)
</script>

<style scoped lang="scss">
.member-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button.is-danger { color: var(--jf-color-error); }
.row-actions button.is-danger:hover { background: rgba(231, 101, 101, .12); }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
